import React, { useEffect, useState } from 'react'
import { X, FileText, MessageSquare, Layers, Clock, Zap, Brain } from 'lucide-react'
import api from '../../services/api'

interface ResourceDetailsModalProps {
  resourceId: string
  onClose: () => void
}

interface ResourceDetails {
  id: string
  name: string
  description: string
  resource_type: string
  mime_type: string
  size_bytes: number
  total_tokens: number
  chunk_count: number
  query_count: number
  last_queried_at: string | null
  created_at: string
  updated_at: string
  tags: string[]
  recent_queries: Array<{
    query: string
    answer: string
    strategy: string
    tokens_used: number
    chunks_retrieved: number
    confidence: string
    created_at: string
  }>
}

const ResourceDetailsModal: React.FC<ResourceDetailsModalProps> = ({ resourceId, onClose }) => {
  const [resource, setResource] = useState<ResourceDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showChunks, setShowChunks] = useState(false)
  const [chunks, setChunks] = useState<any[]>([])
  const [loadingChunks, setLoadingChunks] = useState(false)

  useEffect(() => {
    fetchResourceDetails()
  }, [resourceId])

  const fetchResourceDetails = async () => {
    try {
      setLoading(true)
      const response = await api.resources.get(resourceId)
      setResource(response.resource)
      setError(null)
    } catch (err) {
      console.error('Error fetching resource details:', err)
      setError('Error al cargar los detalles del recurso')
    } finally {
      setLoading(false)
    }
  }

  const fetchChunks = async () => {
    try {
      setLoadingChunks(true)
      const response = await api.resources.getChunks(resourceId)
      setChunks(response.chunks)
      setShowChunks(true)
    } catch (err) {
      console.error('Error fetching chunks:', err)
      alert('Error al cargar los chunks')
    } finally {
      setLoadingChunks(false)
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca'
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getConfidenceBadge = (confidence: string) => {
    const colors = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    }
    return colors[confidence as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    )
  }

  if (error || !resource) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <p className="text-red-600">{error || 'Recurso no encontrado'}</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileText className="text-blue-600" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{resource.name}</h2>
              <p className="text-sm text-gray-600">{resource.description || 'Sin descripción'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <FileText size={16} />
                <span className="text-sm">Tamaño</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{formatBytes(resource.size_bytes)}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Zap size={16} />
                <span className="text-sm">Tokens</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{(resource.total_tokens / 1000).toFixed(1)}K</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Layers size={16} />
                <span className="text-sm">Chunks</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{resource.chunk_count}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <MessageSquare size={16} />
                <span className="text-sm">Consultas</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{resource.query_count}</p>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 mb-3">Metadatos</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Tipo:</span>
                <span className="ml-2 text-gray-900">{resource.resource_type}</span>
              </div>
              <div>
                <span className="text-gray-600">MIME:</span>
                <span className="ml-2 text-gray-900">{resource.mime_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Creado:</span>
                <span className="ml-2 text-gray-900">{formatDate(resource.created_at)}</span>
              </div>
              <div>
                <span className="text-gray-600">Última consulta:</span>
                <span className="ml-2 text-gray-900">{formatDate(resource.last_queried_at)}</span>
              </div>
            </div>
          </div>

          {/* Tags */}
          {resource.tags.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {resource.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Strategy Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-900 mb-2">
              <Brain size={20} />
              <h3 className="font-semibold">Estrategia RAG</h3>
            </div>
            <p className="text-sm text-blue-800">
              {resource.chunk_count === 1
                ? 'Este recurso usa contexto completo aprovechando la ventana de 131K tokens de Granite.'
                : `Este recurso usa búsqueda semántica híbrida con ${resource.chunk_count} chunks. Las consultas recuperan los 5 chunks más relevantes.`}
            </p>
          </div>

          {/* Chunks Debug */}
          {resource.chunk_count > 1 && (
            <div>
              <button
                onClick={fetchChunks}
                disabled={loadingChunks}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                {loadingChunks ? 'Cargando chunks...' : showChunks ? 'Ocultar chunks' : 'Ver chunks (debug)'}
              </button>

              {showChunks && chunks.length > 0 && (
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                  {chunks.map((chunk) => (
                    <div key={chunk.id} className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">Chunk {chunk.chunk_index}</span>
                        <span className="text-gray-600">{chunk.token_count} tokens</span>
                      </div>
                      <p className="text-gray-700 text-xs line-clamp-3">{chunk.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recent Queries */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Consultas Recientes</h3>
            {resource.recent_queries.length === 0 ? (
              <p className="text-gray-600 text-sm">No hay consultas aún</p>
            ) : (
              <div className="space-y-3">
                {resource.recent_queries.map((q, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-gray-900 text-sm flex-1">{q.query}</p>
                      <span className={`px-2 py-1 rounded text-xs ${getConfidenceBadge(q.confidence)}`}>
                        {q.confidence}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">{q.answer}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatDate(q.created_at)}
                      </span>
                      <span>{q.strategy === 'full-context' ? 'Contexto completo' : 'Búsqueda semántica'}</span>
                      <span>•</span>
                      <span>{q.tokens_used} tokens</span>
                      <span>•</span>
                      <span>{q.chunks_retrieved} chunks</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default ResourceDetailsModal
